import React, {memo, useEffect, useRef} from "react";
import PropTypes from "prop-types";
import {Box, Drawer, IconButton, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {useMutation} from "@apollo/client";
import NotificationTabs from "./NotificationTabs";
import {MARK_NOTIFICATIONS_AS_SEEN} from "../../graphql/notificationCenterQueries";
import {useNotificationDrawerStyles} from "./notificationCenterStyles";

const NotificationDrawer = ({
  open,
  isSidebarAnimating,
  onClose,
  sidebarWidth,
  resetUnseenCounts,
}) => {
  const {classes} = useNotificationDrawerStyles();
  const visitedCategoriesRef = useRef(new Set());
  const [markAsSeen] = useMutation(MARK_NOTIFICATIONS_AS_SEEN);
  const markAsSeenRef = useRef(markAsSeen);
  const resetUnseenCountsRef = useRef(resetUnseenCounts);
  markAsSeenRef.current = markAsSeen;
  resetUnseenCountsRef.current = resetUnseenCounts;

  useEffect(() => {
    if (open) {
      return () => {
        visitedCategoriesRef.current.forEach((cat) => {
          markAsSeenRef.current({variables: {category: cat}});
          resetUnseenCountsRef.current(cat);
        });
        visitedCategoriesRef.current = new Set();
      };
    }
  }, [open]);

  return (
    <Drawer
      anchor="left"
      variant="temporary"
      open={open && !isSidebarAnimating}
      onClose={onClose}
      ModalProps={{
        disableScrollLock: true,
        disableEnforceFocus: true,
        disableAutoFocus: true,
      }}
      slotProps={{
        backdrop: {invisible: true},
      }}
      PaperProps={{
        className: classes.paper,
        sx: {marginLeft: `${sidebarWidth}px`},
      }}
      data-testid="notification-drawer"
    >
      <Box className={classes.drawerHeader}>
        <Typography variant="h6">Notifications</Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </Box>
      <NotificationTabs
        onClose={onClose}
        onCategoryVisited={(category) => {
          visitedCategoriesRef.current.add(category);
        }}
      />
    </Drawer>
  );
};

NotificationDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  isSidebarAnimating: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sidebarWidth: PropTypes.number.isRequired,
  resetUnseenCounts: PropTypes.func.isRequired,
};

export default memo(NotificationDrawer);
